import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  createFinanceCategory,
  getFinanceSettings,
  updateFinanceCategory,
  updateFinanceSettings,
} from '../services/api';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';

function FinanceSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ quoteLeadDays: 3, invoiceLeadDays: 5 });
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', tooltip: '', parentId: '', appliesTo: 'BOTH', expectsProject: false, warnIfUnassigned: false });

  const load = async () => {
    const res = await getFinanceSettings();
    setSettings(res.data.settings || settings);
    setCategories(res.data.categories || []);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const parents = categories.filter((c) => !c.parentId);

  return (
    <ResponsivePageSection>
      <Typography variant="h4" sx={{ mb: 2 }}>{t('finance.settings')}</Typography>
      <Button onClick={() => navigate('/finance/quotes')} sx={{ mb: 2 }}>{t('finance.back')}</Button>
      <Stack spacing={2} sx={{ maxWidth: 480, mb: 4 }}>
        <TextField type="number" label={t('finance.quoteLeadDays')} helperText={t('finance.quoteLeadDaysHint')} value={settings.quoteLeadDays} onChange={(e) => setSettings((p) => ({ ...p, quoteLeadDays: e.target.value }))} />
        <TextField type="number" label={t('finance.invoiceLeadDays')} helperText={t('finance.invoiceLeadDaysHint')} value={settings.invoiceLeadDays} onChange={(e) => setSettings((p) => ({ ...p, invoiceLeadDays: e.target.value }))} />
        <Button variant="contained" onClick={() => updateFinanceSettings({ quoteLeadDays: Number(settings.quoteLeadDays), invoiceLeadDays: Number(settings.invoiceLeadDays) })}>{t('common.save')}</Button>
      </Stack>
      <Typography variant="h6">{t('finance.categories')}</Typography>
      <Button sx={{ my: 1 }} onClick={() => setOpen(true)}>{t('finance.addCategory')}</Button>
      {parents.map((parent) => (
        <Box key={parent.id} sx={{ mb: 2 }}>
          <Typography fontWeight={600}>{parent.name} {parent.active ? '' : `(${t('finance.inactive')})`}</Typography>
          <Typography variant="caption">{parent.tooltip}</Typography>
          <Button size="small" onClick={() => updateFinanceCategory(parent.id, { active: !parent.active })}>{parent.active ? t('finance.deactivate') : t('finance.activate')}</Button>
          {categories.filter((c) => c.parentId === parent.id).map((child) => (
            <Typography key={child.id} sx={{ pl: 2 }}>— {child.name}</Typography>
          ))}
        </Box>
      ))}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t('finance.addCategory')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label={t('finance.name')} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            <TextField label={t('finance.tooltip')} value={form.tooltip} onChange={(e) => setForm((p) => ({ ...p, tooltip: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>{t('finance.parent')}</InputLabel>
              <Select label={t('finance.parent')} value={form.parentId} onChange={(e) => setForm((p) => ({ ...p, parentId: e.target.value }))}>
                <MenuItem value="">{t('finance.none')}</MenuItem>
                {parents.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel control={<Checkbox checked={form.expectsProject} onChange={(e) => setForm((p) => ({ ...p, expectsProject: e.target.checked }))} />} label={t('finance.expectsProject')} />
            <FormControlLabel control={<Checkbox checked={form.warnIfUnassigned} onChange={(e) => setForm((p) => ({ ...p, warnIfUnassigned: e.target.checked }))} />} label={t('finance.warnIfUnassigned')} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={async () => {
            await createFinanceCategory({ ...form, parentId: form.parentId || null });
            setOpen(false);
            load();
          }}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </ResponsivePageSection>
  );
}

export default FinanceSettings;
